import * as React from 'react';
import { Virtualizer } from '@tanstack/react-virtual';
import { TwiddleOpenContextType } from '../components/NewTableTwiddle/contexts';
import { VirtualizerOptions, VirtualRowExtension, ParentTwiddle, ChildTwiddle, VirtualRow } from './types';

/*
  These numbers come from the computed height of each
  different NewTable.Row density, and are used to estimate
  the height of each row in the virtualizer. I don't know
  why they are fractional values in the browser, but I observed
  less shift in the wrapper's `height` value when these were
  integers.
*/
export const densityHeightMap = {
  higher: 33,
  high: 33,
  normal: 45,
  low: 65,
} as const;

type UseFlatDataArrayArgs<DataItem, Container extends Window | HTMLDivElement> = {
  data: DataItem[];
  rowsState: TwiddleOpenContextType | undefined;
  getTwiddleRowCount: VirtualizerOptions<DataItem, Container>['getTwiddleRowCount'];
  getItemKey: VirtualizerOptions<DataItem, Container>['getItemKey'];
};

/**
 * This hook transforms the data passed into the hook, appending information
 * about whether a row is a parent or child of a twiddle row.
 *
 * The hook utilizes `React.useMemo` to optimize performance, recalculating the output only when
 * the `data`, `getTwiddleRowCount`, or `getItemKey` inputs change, or when there are changes in the
 * open state of rows via `rowsState`. If this table has no twiddles, we never get past the first if
 * statement.
 *
 * Parent rows are always included for each data item. Child rows are included based on the
 * current `rowsState`, specifically if the row is expanded or if all rows are open.
 * If `getTwiddleRowCount` or `getItemKey` are not provided, it defaults to creating only parent
 * rows for each data item.
 */
export const useFlatDataArray = <DataItem, Container extends Window | HTMLDivElement>({
  data,
  rowsState,
  getTwiddleRowCount,
  getItemKey,
}: UseFlatDataArrayArgs<DataItem, Container>): Array<VirtualRowExtension> => {
  const { getOpenState } = rowsState || {};

  return React.useMemo(() => {
    if (!getTwiddleRowCount || !getItemKey) {
      return data.map((_, rowIndex) => ({
        type: 'parent',
        rowIndex,
      }));
    }

    return data.flatMap((dataItem, rowIndex) => {
      const key = getItemKey(rowIndex);

      const parentRow: ParentTwiddle = {
        type: 'parent' as const,
        key: `${key}-p`,
        rowIndex,
      };

      const open = getOpenState?.(key);

      if (!open) {
        return parentRow;
      }

      const children: ChildTwiddle[] = Array(getTwiddleRowCount(dataItem))
        .fill(null)
        .map((_, twiddleIndex) => ({
          type: 'child',
          rowIndex,
          twiddleIndex,
        }));

      return [parentRow, ...children];
    });
  }, [data, getItemKey, getOpenState, getTwiddleRowCount]);
};

type UseVirtualCallbacksArgs<DataItem, Container extends Window | HTMLDivElement> = {
  virtualizer: Virtualizer<Container, HTMLTableRowElement>;
  flatData: VirtualRowExtension[];
  parentRef: React.RefObject<HTMLDivElement>;
  data: DataItem[];
  setTwiddleState: React.Dispatch<React.SetStateAction<TwiddleOpenContextType>>;
  type: 'window' | 'container';
};

export const useVirtualCallbacks = <DataItem, Container extends Window | HTMLDivElement>({
  virtualizer,
  flatData,
  parentRef,
  data,
  setTwiddleState,
  type,
}: UseVirtualCallbacksArgs<DataItem, Container>) => {
  /** Returns an array of virtual items corresponding to the currently visible rows. */
  const getVirtualItems = React.useCallback((): VirtualRow[] => {
    return virtualizer.getVirtualItems().map(virtualRow => ({
      ...virtualRow,
      ...flatData[virtualRow.index],
    }));
  }, [flatData, virtualizer]);

  /** Call and destructure its returned object on `<NewTable.VirtualWrapper>`. */
  const getWrapperProps = React.useCallback(
    () => ({
      ref: parentRef,
      type,
    }),
    [parentRef, type],
  );

  /** Call and destructure its returned object on `<NewTable.Head>`. */
  const getBodyProps = React.useCallback(
    () => ({
      height: virtualizer.getTotalSize(),
      onTwiddleContextMount: setTwiddleState,
    }),
    [virtualizer, setTwiddleState],
  );

  /** Call with `virtualRow` and destructure its returned object on each `<NewTable.Row>` */
  const getRowProps = React.useCallback(
    (virtualRow: VirtualRow) => ({
      'data-index': virtualRow.index,
      key: virtualRow.key,
      ref: virtualRow.measureElement,
      transform: `translateY(${virtualRow.start - (virtualizer.options.scrollMargin ?? 0)}px)`,
    }),
    [virtualizer],
  );

  /** Call with `virtualRow` to retrieve the virtual row's corresponding data item. */
  const getRowItem = (virtualRow: VirtualRow) => {
    if (virtualRow.type === 'parent') {
      return data[virtualRow.rowIndex];
    }
    return data[flatData[virtualRow.index].rowIndex];
  };

  return {
    ...virtualizer,
    getVirtualItems,
    getWrapperProps,
    getBodyProps,
    getRowProps,
    getRowItem,
  };
};
